/**
 * Block vertical spacing (stored as marginTop / marginBottom).
 *
 * Legacy values are bare numbers and mean pixels. Newer values are
 * `{ value, unit }` so the inspector can offer px / rem / em / vh.
 */

export const SPACE_UNITS = ['px', 'rem', 'em', 'vh'];

export const SPACE_LIMITS = {
    px: { min: 0, max: 400, step: 1 },
    rem: { min: 0, max: 25, step: 0.1 },
    em: { min: 0, max: 25, step: 0.1 },
    vh: { min: 0, max: 50, step: 0.1 },
};

/**
 * @param {unknown} raw
 * @returns {{ value: number, unit: string }}
 */
export function parseSpace(raw) {
    if (raw == null || raw === '') return { value: 0, unit: 'px' };
    if (typeof raw === 'number') {
        const value = Number.isFinite(raw) ? Math.max(0, raw) : 0;
        return { value, unit: 'px' };
    }
    if (typeof raw === 'object' && raw !== null) {
        const unit = SPACE_UNITS.includes(raw.unit) ? raw.unit : 'px';
        const value = Number(raw.value);
        return { value: Number.isFinite(value) ? Math.max(0, value) : 0, unit };
    }
    const match = String(raw).trim().match(/^(-?[\d.]+)\s*(px|rem|em|vh)?$/i);
    if (!match) return { value: 0, unit: 'px' };
    const value = Number(match[1]);
    const unit = SPACE_UNITS.includes((match[2] || 'px').toLowerCase())
        ? (match[2] || 'px').toLowerCase()
        : 'px';
    return { value: Number.isFinite(value) ? Math.max(0, value) : 0, unit };
}

/** Clamp a value for its unit. */
export function clampSpace(value, unit = 'px') {
    const limits = SPACE_LIMITS[unit] || SPACE_LIMITS.px;
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    const stepped = limits.step < 1 ? Math.round(n / limits.step) * limits.step : Math.round(n);
    const rounded = limits.step < 1 ? Math.round(stepped * 1000) / 1000 : stepped;
    return Math.max(limits.min, Math.min(limits.max, rounded));
}

/**
 * Persist form. Zero clears the field. Bare numbers stay numbers when unit is px
 * so existing pages keep a compact shape.
 * @param {number|object|string|null|undefined} raw
 * @param {string} [unit]
 */
export function serializeSpace(raw, unit) {
    const parsed = unit != null
        ? { value: Number(raw) || 0, unit: SPACE_UNITS.includes(unit) ? unit : 'px' }
        : parseSpace(raw);
    const value = clampSpace(parsed.value, parsed.unit);
    if (!value) return undefined;
    if (parsed.unit === 'px') return value;
    return { value, unit: parsed.unit };
}

/** CSS length for margin/padding, or undefined when zero. */
export function cssSpace(raw) {
    const { value, unit } = parseSpace(raw);
    if (!value) return undefined;
    return `${clampSpace(value, unit)}${unit}`;
}

export function hasSpace(raw) {
    return parseSpace(raw).value > 0;
}

/**
 * Gallery gap for masonry and directory.
 *
 * A bare number is the original control: 0–20, drawn as that many vw split
 * across the columns. Choosing a unit stores `{ value, unit }` and the length
 * is used as written.
 */
export const GAP_UNITS = ['px', '%', 'rem', 'em', 'vh', 'vw'];

export const GAP_LIMITS = {
    px: { min: 0, max: 200, step: 1 },
    '%': { min: 0, max: 100, step: 0.1 },
    rem: { min: 0, max: 16, step: 0.1 },
    em: { min: 0, max: 16, step: 0.1 },
    vh: { min: 0, max: 40, step: 0.1 },
    vw: { min: 0, max: 40, step: 0.1 },
};

export function clampGap(value, unit = 'vw') {
    const limits = GAP_LIMITS[unit] || GAP_LIMITS.vw;
    const n = Number(value);
    if (!Number.isFinite(n)) return limits.min;
    const stepped = limits.step < 1 ? Math.round(n / limits.step) * limits.step : Math.round(n);
    const rounded = limits.step < 1 ? Math.round(stepped * 1000) / 1000 : stepped;
    return Math.max(limits.min, Math.min(limits.max, rounded));
}

/**
 * @param {unknown} raw
 * @returns {{ value: number, unit: string, legacy: boolean }}
 */
export function parseGap(raw) {
    if (raw == null || raw === '') return { value: 4, unit: 'vw', legacy: true };
    if (typeof raw === 'number') {
        const n = Number.isFinite(raw) ? raw : 4;
        return { value: Math.max(0, Math.min(20, Math.round(n))), unit: 'vw', legacy: true };
    }
    if (typeof raw === 'object') {
        const unit = GAP_UNITS.includes(raw.unit) ? raw.unit : 'px';
        return { value: clampGap(raw.value, unit), unit, legacy: false };
    }
    const match = String(raw).trim().match(/^([\d.]+)\s*(px|%|rem|em|vh|vw)?$/i);
    if (!match || !match[2]) {
        const n = match ? Number(match[1]) : 4;
        return { value: Math.max(0, Math.min(20, Math.round(Number.isFinite(n) ? n : 4))), unit: 'vw', legacy: true };
    }
    const unit = match[2].toLowerCase();
    return { value: clampGap(match[1], unit), unit, legacy: false };
}

/**
 * @param {number|string} raw
 * @param {string} unit
 * @param {{ legacy?: boolean }} [options] legacy keeps the original 0–20 number
 */
export function serializeGap(raw, unit, { legacy = false } = {}) {
    const nextUnit = GAP_UNITS.includes(unit) ? unit : 'vw';
    if (legacy && nextUnit === 'vw') {
        const n = Math.round(Number(raw));
        if (!Number.isFinite(n)) return 4;
        return Math.max(0, Math.min(20, n));
    }
    return { value: clampGap(raw, nextUnit), unit: nextUnit };
}

/** CSS length for the column gap and the matching side inset. */
export function galleryGapCss(raw, columnCount = 1) {
    const parsed = parseGap(raw);
    if (parsed.legacy) {
        const cols = Math.max(1, Number(columnCount) || 1);
        return `${parsed.value / cols}vw`;
    }
    return `${parsed.value}${parsed.unit}`;
}

/** Value the page API may store: a 0–20 number, or `{ value, unit }`. */
export function normalizeGalleryGap(raw) {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const parsed = parseGap(raw);
        return { value: parsed.value, unit: parsed.unit };
    }
    return parseGap(raw).value;
}

/**
 * Convert a pixel delta from a drag into the stored unit.
 * @param {number} px
 * @param {string} unit
 * @param {Element} [el]
 */
export function pxToSpaceValue(px, unit, el) {
    const safe = Math.max(0, px);
    if (unit === 'px') return clampSpace(safe, 'px');
    if (unit === 'vh') {
        const vh = typeof window !== 'undefined' ? window.innerHeight || 1 : 1;
        return clampSpace((safe / vh) * 100, 'vh');
    }
    if (unit === 'rem') {
        const root = typeof document !== 'undefined'
            ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
            : 16;
        return clampSpace(safe / root, 'rem');
    }
    if (unit === 'em') {
        const base = el && typeof getComputedStyle === 'function'
            ? parseFloat(getComputedStyle(el).fontSize) || 16
            : 16;
        return clampSpace(safe / base, 'em');
    }
    return clampSpace(safe, 'px');
}

/**
 * Read the current spacing of a block as rendered pixels (for drag start).
 * Falls back to converting the stored value when the element is missing.
 */
export function spaceToPx(raw, el) {
    const { value, unit } = parseSpace(raw);
    if (!value) return 0;
    if (unit === 'px') return value;
    if (unit === 'vh') {
        const vh = typeof window !== 'undefined' ? window.innerHeight || 0 : 0;
        return (value / 100) * vh;
    }
    if (unit === 'rem') {
        const root = typeof document !== 'undefined'
            ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
            : 16;
        return value * root;
    }
    if (unit === 'em') {
        const base = el && typeof getComputedStyle === 'function'
            ? parseFloat(getComputedStyle(el).fontSize) || 16
            : 16;
        return value * base;
    }
    return value;
}

export default {
    SPACE_UNITS,
    SPACE_LIMITS,
    parseSpace,
    clampSpace,
    serializeSpace,
    cssSpace,
    hasSpace,
    GAP_UNITS,
    GAP_LIMITS,
    clampGap,
    parseGap,
    serializeGap,
    galleryGapCss,
    normalizeGalleryGap,
    pxToSpaceValue,
    spaceToPx,
};
